// calculation-reducer.ts
import { createReducer, on } from '@ngrx/store';
import { CalculationSelection, CalculationState, ViewModelLineType, createEmptyAggregation, initialCalculationState, isCalculationLineApi, mergeAggregatedValues } from './calculation-state';
import { calculationRedo, calculationUndo, calculationUndoRedoDone, deleteTailLineSuccess, freezeCalculation, freezeCalculationSuccess, insertLine, insertLineFailure, insertLineSuccess, insertLines, insertLinesSuccess, insertTailLineFailure, insertTailLineSuccess, loadCalculation, loadCalculationFailure, loadCalculationSuccess, loadRefCalculation, loadRefCalculationFailure, loadRefCalculationSuccess, moveSelectionDown, moveSelectionLeft, moveSelectionRight, moveSelectionUp, deleteLine, deleteLines, undeleteLineFailure, unlockCalculation, unlockCalculationFailure, unlockCalculationSuccess, updateGrandTotals, updateLineUi, updateLineFailure, updateMeta, updateMetaFailure, updateSelection, updateTailLineUi as updateTailUi, updateTailLineFailure, updateWarnings, variationsFromCalculation, deleteLineSuccess, deleteLinesSuccess, deleteLineFailure, deleteLinesFailure, undeleteLine, undeleteLineSuccess, deleteTailLine, deleteTailLineFailure, deleteTailLines, deleteTailLinesSuccess, createCalculation, createCalculationSuccess, calculationUndoRedoFailure, bulkUndeleteLinesFailure, bulkDeleteLinesFailure, bulkUpdateLinesFailure, pdfExport, pdfExportFailure, pdfExportCreationSuccess, pdfExportDownloadSuccess, bulkUpdateLinesSuccess, startScrolling, updateVisibleParts, updateTableLayout, bulkUpdateLines, updateCalculationLineStale, updateTailLineStale, bulkUpdateTailLines, bulkUpdateTailLinesFailure, bulkInsertTailLines, bulkInsertTailLinesFailure, showSideMenu, hideSideMenu, updateTailLineUi, insertTailLine, bulkInsertTailLinesSuccess } from './calculation-actions';
import { CalculationLineApi, CalculationMetaApi, CalculationPercentagesApi, CalculationTailApi, TemplateCategoryApi } from '../types';
import { createViewModel } from './functions/convert-calculation-line';
import { ColumnDefinition, ColumnFormat, CONDENSED_COLUMN_DEFINITION, DEFAULT_COLUMN_DEFINITION } from '../constants';
import { addHeaderLineFailure as addHeaderLineFailure, addHeaderLine, addHeaderLineSuccess as addHeaderLineSuccess, updateHeaderLine, updateHeaderLineFailure, updateHeaderLineSuccess, deleteHeaderLine, deleteHeaderLineFailure, deleteHeaderLineSuccess } from './actions/header-line-actions';

export const calculationReducer = createReducer(
    initialCalculationState,
    // On loading calculation
    on(loadCalculation, (state): CalculationState => {
        // console.log('State', state);
        return {
            ...state,
            // meta: undefined,
            lines: [],
            hourRates: [],
            viewModelLines: [],
            currentSelection: undefined,
            loading: true,
            warnings: undefined,
            template: undefined,
            templateDict: undefined
        }
    }),
    on(loadCalculationSuccess, (state, { calculation }) => {
        // Flatten the lines of the data model to a list
        const dataModelLines = calculation.categories?.flatMap(category => category.subCategories.flatMap(sub => sub.lines)) ?? [];
        // Convert the template to a dictionary for category and subcategory descriptions. This information is not part of the data model, but the template
        const dict = convertTemplateToDict(calculation.template);
        // Convert the data model to the view model
        const updatedState = createViewModel({
            ...state,
            lines: dataModelLines,
            meta: { ...calculation.meta },
            hourRates: [...calculation.header].sort((r1, r2) => (r1.sortOrder ?? 0) - (r2.sortOrder ?? 0)),
            template: [...calculation.template],
            templateDict: dict,
            tail: calculation.tail,
        });

        return ({
            ...updatedState,
            loading: false,
            maxVersion: isSameCalculation(state, calculation.meta) ? Math.max(state.maxVersion ?? 1, calculation.meta.currentVersion ?? 1) : calculation.meta.currentVersion,
            // This will be the same until reload
            aggregationOnLoad: mergeAggregatedValues(createEmptyAggregation(), updatedState.aggregation),
            show: 'all'

        } satisfies CalculationState)
    }),
    // On creation
    on(createCalculation, (state): CalculationState => {
        return {
            ...state,
            loading: true
        }
    }),
    on(createCalculationSuccess, (state, { }) => {
        return {
            ...state,
            loading: false
        }
    }),
    on(updateGrandTotals, (state, { aggregation }) => ({
        ...state,
        aggregation
    })),
    on(loadCalculationFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error: error
    })),
    // On loading calculation
    on(loadRefCalculation, (state): CalculationState => {
        // console.log('State', state);
        return {
            ...state,
            loading: true,
            referenceCalculation: undefined
        }
    }),
    on(loadRefCalculationSuccess, (state, { calculation }) => {
        // console.log('Previous lines:', state.lines);
        return ({
            ...state,
            loading: false,
            referenceCalculation: calculation.categories?.flatMap(category => category.subCategories.flatMap(sub => sub.lines))
        })
    }),
    on(loadRefCalculationFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error: error
    })),
    on(updateSelection, (state, { selection }) => {
        // Add the selected view model lines to the state
        const originRowIndex = selection.origin?.row ?? selection.current.row;

        // This is a decent approach, but has one flaw:
        // Subtotal lines are also linked to calculation lines, so a line with endOfSection === true will show up twice
        // Not completely clean, but we can fix it by filtering out recurring ids
        const selectedLines = [...state.viewModelLines.slice(originRowIndex, selection.current.row + 1)]
            .filter((f, i, a) => !!f.line && a.findIndex(fnd => fnd.line?.id === f.line?.id) === i)
            .map(m => m.line) as (CalculationLineApi | CalculationTailApi)[];

        return {
            ...state,
            currentSelection: {
                ...selection,
                selectedLines
            }
        }
    }),
    on(updateLineUi, (state, { line }) => {
        const dataModelLines = [...state.lines.filter(f => f.id !== line.id), line];

        // Check if the line is being created, if so; add to the staleIds
        const newStaleIds = (state.creatingIds[line.id]) ? {
            ...state.staleIds,
            [line.id]: true
        }
            : state.staleIds;

        // Convert the data model to the view model
        const updatedState = createViewModel({ ...state, lines: dataModelLines });

        return {
            ...updatedState,
            ...newStaleIds,
        }
    }),
    on(updateCalculationLineStale, (state, { line }) => {
        const dataModelLines = [...state.lines.filter(f => f.id !== line.id), line];
        // Convert the data model to the view model
        const updatedState = createViewModel({ ...state, lines: dataModelLines });

        const { [line.id]: _, ...newStale } = state.staleIds;

        return {
            ...updatedState,
            staleIds: newStale,
        }
    }),
    on(updateLineFailure, (state, { error }) => {
        return {
            ...state,
            error: error
        }
    }),
    on(insertLine, (state, { line }) => {
        console.log('[REDUCER] insertLine ontvangen');

        return createViewModel({
            ...state,
            currentSelection: {
                ...state.currentSelection,
                selectedLines: [line]
            },
            lines: [...state.lines, line],
            creatingIds: {
                ...state.creatingIds,
                [line.id]: true,
            },
        });
    }),
    on(insertLineSuccess, (state, { line }) => {
        console.log('[REDUCER] insertLineSuccess ontvangen');

        const { [line.id]: _, ...newCreating } = state.creatingIds;
        // const updatedState = createViewModel({ ...state });
        return ({
            ...state,
            creatingIds: newCreating,
        })
    }),
    on(insertLineFailure, (state, { error }) => {
        return {
            ...state,
            error: error,
            loading: false
        }
    }),
    on(deleteLine, (state, { projectId, calculationId, line }) => {
        return {
            ...updateSelectionOnDeletion(state, [line]),
            loading: true
        }
    }),
    on(deleteLineSuccess, (state, { line }) => {
        const dataModelLines = [...state.lines.filter(f => f.id !== line.id)];
        // Convert the data model to the view model
        const updatedState = createViewModel({ ...state, lines: dataModelLines });
        return {
            ...updatedState,
            loading: false
        }
    }),
    on(deleteLineFailure, (state, { error }) => {
        return ({
            ...state,
            error: error,
            loading: false
        })
    }),
    on(deleteLines, (state, { projectId, calculationId, lines }) => {
        // Since there is no backend endpoint for bulk deleting lines,
        // activate loading state, the effect will delete the lines one by one.
        // After that, it will call the finishDeleteLines function that will update the lines
        return {
            ...updateSelectionOnDeletion(state, lines),
            loading: true
        }
    }),
    on(deleteLinesSuccess, (state, { lines }) => {
        const deleteIds = lines.map(m => m.id);
        const dataModelLines = [...state.lines.filter(f => !deleteIds.includes(f.id))];
        // Convert the data model to the view model
        const updatedState = createViewModel({ ...state, lines: dataModelLines });
        return {
            ...updatedState,
            loading: false
        }
    }),
    on(deleteLinesFailure, (state, { error }) => {
        return ({
            ...state,
            error: error,
            loading: false
        })
    }),
    on(updateTailLineUi, (state, { line }) => {
        const tailLines = [...state.tail.filter(f => f.id !== line.id), line];
        // Convert the data model to the view model
        const updatedState = createViewModel({ ...state, tail: tailLines });

        return {
            ...updatedState
        }
    }),
    on(updateTailLineStale, (state, { line }) => {
        const dataModelLines = [...state.tail.filter(f => f.id !== line.id), line];
        // Convert the data model to the view model
        const updatedState = createViewModel({ ...state, tail: dataModelLines });

        const { [line.id]: _, ...newStale } = state.staleIds;

        return {
            ...updatedState,
            staleIds: newStale,
        }
    }),
    on(updateTailLineFailure, (state, { error }) => {
        return {
            ...state,
            error: error,
            loading: false
        }
    }),
    on(insertTailLine, (state, { line }) => {

        return createViewModel({
            ...state,
            currentSelection: {
                ...state.currentSelection,
                selectedLines: [line]
            },
            tail: [...state.tail, line],
            creatingIds: {
                ...state.creatingIds,
                [line.id]: true,
            },
        });
    }),
    on(insertTailLineSuccess, (state, { line }) => {
        const { [line.id]: _, ...newCreating } = state.creatingIds;
        // const updatedState = createViewModel({ ...state });
        return ({
            ...state,
            creatingIds: newCreating,
        })
    }),
    on(insertTailLineFailure, (state, { error }) => {
        return {
            ...state,
            error: error,
            loading: false
        }
    }),
    on(deleteTailLine, (state, { projectId, calculationId, line }) => {
        return {
            ...updateSelectionOnDeletion(state),
            loading: true
        }
    }),
    on(deleteTailLineSuccess, (state, { line }) => {
        const tailLines = [...state.tail.filter(f => f.id !== line.id)];
        // Convert the data model to the view model
        const updatedState = createViewModel({ ...state, tail: tailLines });
        return {
            ...updatedState,
            loading: false
        }
    }),
    on(deleteTailLineFailure, (state, { error }) => {
        return {
            ...state,
            error: error,
            loading: false
        }
    }),
    on(deleteTailLines, (state, { projectId, calculationId, lines }) => {
        return {
            ...updateSelectionOnDeletion(state, lines),
            loading: true
        }
    }),
    on(deleteTailLinesSuccess, (state, { lines }) => {
        const deleteIds = lines.map(m => m.id);
        const tailLines = [...state.tail.filter(f => !deleteIds.includes(f.id))];
        // Convert the data model to the view model
        const updatedState = createViewModel({ ...state, tail: tailLines });
        return {
            ...updatedState,
            loading: false
        }
    }),
    on(deleteTailLineFailure, (state, { error }) => {
        return {
            ...state,
            error: error,
            loading: false
        }
    }),
    on(deleteHeaderLine, (state, { projectId, calculationId, rate }) => {
        return {
            ...state,
            loading: true
        }
    }),
    on(deleteHeaderLineSuccess, (state, { rate }) => {
        const headerLines = [...state.hourRates.filter(f => f.id !== rate.id)];
        // Convert the data model to the view model
        const updatedState = createViewModel({ ...state, hourRates: headerLines });
        return {
            ...updatedState,
            loading: false
        }
    }),
    on(deleteHeaderLineFailure, (state, { error }) => {
        return {
            ...state,
            error: error,
            loading: false
        }
    }),
    on(insertLines, (state, { lines }) =>
        createViewModel({
            ...state,
            currentSelection: {
                ...state.currentSelection,
                selectedLines: lines
            },
            lines: [...state.lines, ...lines],
            creatingIds: {
                ...state.creatingIds,
                ...Object.fromEntries(lines.map(line => [line.id, true]))
            }
        })),
    on(insertLinesSuccess, (state, { lines }) => {
        const ids = lines.map(line => line.id)

        return {
            ...state,
            creatingIds: removeKeys(state.creatingIds, ids),
        }
    }),
    on(updateMeta, (state, { meta }) => {
        // If percentages are updated, also recalculate
        const costCats: (keyof CalculationPercentagesApi)[] = ['hours', 'equipment', 'material', 'subcontracting'];

        if (costCats.some(key => (state.meta?.percentages?.[key] ?? 0) !== (meta?.percentages?.[key] ?? 0))) {
            return createViewModel({ ...state, meta: meta });
        }

        return ({
            ...state,
            meta: { ...meta }
        })
    }),
    on(updateMetaFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error: error
    })),
    on(updateWarnings, (state, { warnings }) => ({
        ...state,
        warnings: warnings
    })),
    on(variationsFromCalculation, (state): CalculationState => {
        // console.log('State', state);
        return {
            ...state,
            loading: true
        }
    }),
    on(loadRefCalculationSuccess, (state, { calculation }) => {
        // console.log('Previous lines:', state.lines);
        return ({
            ...state,
            loading: false
        })
    }),
    on(loadRefCalculationFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error: error
    })),
    on(unlockCalculation, (state): CalculationState => {
        // console.log('State', state);
        return {
            ...state,
            loading: true
        }
    }),
    on(unlockCalculationSuccess, (state, { projectId, calculationId }) => {
        // console.log('Previous lines:', state.lines);
        return ({
            ...state,
            loading: false
        })
    }),
    on(unlockCalculationFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error: error
    })),
    on(freezeCalculation, (state, { projectId, calculationId, version }) => {
        return {
            ...state,
            loading: true
        }
    }),
    on(freezeCalculationSuccess, (state, { meta }) => {
        return {
            ...state,
            loading: false
        }
    }),
    on(moveSelectionDown, (state) => findNextRowSelection(state, false)),
    on(moveSelectionUp, (state) => findNextRowSelection(state, true)),
    on(moveSelectionLeft, (state) => {
        let selectedRow = state.currentSelection.current.row;
        let viewLine = state.viewModelLines[selectedRow];

        if (!viewLine) {
            return { ...state };
        }

        // Get the column definition
        const colDef = state.columns[viewLine.type];

        // Find the next selectable column
        const leftSelectable = Object.keys(colDef) // Turn the column map into column id array
            .map(k => colDef[k]) // Map column id to column array
            .reverse() // Reverse the order so we're looking from right to left
            .find((column) => {
                // Ignore all columns right of the current selection
                if (column.index >= state.currentSelection.current.column) return false;

                // Return if the column can be focussed and selected and isn't hidden
                return column.selectable && column.focusable && !column.hidden;
            });

        // If we found a valid column, assign it
        if (leftSelectable) {
            return {
                ...state,
                currentSelection: {
                    current: {
                        column: leftSelectable.index,
                        row: selectedRow
                    },
                    selectedLines: [state.viewModelLines[selectedRow].line]
                }
            } satisfies CalculationState
        } else {
            return { ...state }
        }
    }),
    on(moveSelectionRight, (state) => {
        let selectedRow = state.currentSelection?.current.row;
        let viewLine = state.viewModelLines[selectedRow];

        if (!viewLine) {
            return { ...state };
        }

        // Get the column definition
        const colDef = state.columns[viewLine?.type];

        // Find the next selectable column
        const rightSelectable = Object.keys(colDef) // Turn the column map into column id array
            .map(k => colDef[k]) // Map column id to column array
            .find((column) => {
                // Ignore all columns right of the current selection
                if (column.index <= state.currentSelection?.current.column) return false;

                // Return if the column can be focussed and selected and isn't hidden
                return column.focusable && column.selectable && !column.hidden;
            });

        // If we found a valid column, assign it
        if (rightSelectable) {
            return {
                ...state,
                currentSelection: {
                    current: {
                        column: rightSelectable.index,
                        row: selectedRow
                    },
                    selectedLines: [state.viewModelLines[selectedRow].line]
                }
            }
        } else {
            return { ...state }
        }
    }),
    on(calculationUndo, (state, { diffs, reversedState }) => {
        return {
            ...state,
            loading: true
        }
    }),
    on(calculationRedo, (state, { diffs, reversedState }) => {
        return {
            ...state,
            loading: true
        }
    }),
    on(calculationUndoRedoDone, (state, { reversedState }) => {
        // Convert the data model to the view model
        const updatedState = createViewModel({
            ...state,
            lines: reversedState.lines,
            meta: { ...reversedState.meta },
            hourRates: [...reversedState.hourRates],
            template: [...reversedState.template],
            templateDict: reversedState.templateDict,
            tail: reversedState.tail,
        });

        return {
            ...updatedState,
            loading: false
        }
    }),
    on(undeleteLine, (state, { projectId, calculationId, line }) => {
        return {
            ...state,
            loading: true
        }
    }),
    on(undeleteLineSuccess, (state, { line }) => {
        const dataModelLines = [...state.lines, line];
        // Convert the data model to the view model
        const updatedState = createViewModel({ ...state, lines: dataModelLines }, line.id);
        return {
            ...updatedState,
            loading: false
        }
    }),
    on(undeleteLineFailure, (state, { error }) => {
        return {
            ...state,
            error: error,
            loading: false
        }
    }),
    on(addHeaderLine, (state, { rate }) => {
        return {
            ...state,
            loading: true
        }
    }),
    on(addHeaderLineSuccess, (state, { rate }) => {
        return {
            ...state,
            hourRates: [...state.hourRates, rate].sort((r1, r2) => (r1.sortOrder ?? 0) - (r2.sortOrder ?? 0)),
            loading: false
        }
    }),
    on(addHeaderLineFailure, (state, { error }) => {
        return {
            ...state,
            error: error,
            loading: false
        }
    }),
    on(updateHeaderLine, (state, { }) => {
        return {
            ...state,
            loading: true
        }
    }),
    on(updateHeaderLineSuccess, (state, { line }) => {
        // Convert the data model to the view model
        const updatedState = createViewModel({ ...state, hourRates: [...state.hourRates.filter(f => f.id !== line.id), { ...line }].sort((l1, l2) => (l1.sortOrder ?? 0) - (l2.sortOrder ?? 0)) });
        return ({
            ...updatedState,
            loading: false,
        })
    }),
    on(updateHeaderLineFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error: error
    })),
    on(calculationUndoRedoFailure, (state, { error }) => {
        // console.log('TRIGGER')
        return {
            ...state,
            loading: false,
            error
        }
    }),
    on(bulkUndeleteLinesFailure, (state, { error }) => {
        return {
            ...state,
            loading: false,
            error
        }
    }),
    on(bulkDeleteLinesFailure, (state, { error }) => {
        return {
            ...state,
            loading: false,
            error
        }
    }),
    on(bulkUpdateLines, (state, { lines }) => {
        console.debug('[REDUCER] bulkUpdateLines ontvangen', lines, state.creatingIds);
        const lineIds = lines.map(m => m.id);
        const untouchedLines = state.lines.filter(f => !lineIds.includes(f.id));
        const updatedLines = [...untouchedLines, ...lines];
        
        // Check if the line is being created, if so; add to the staleIds
        const newStaleIds = lines.reduce<{ [key: string]: boolean }>((p, c) => {
            if (state.creatingIds[c.id]) {
                p[c.id] = true;
            }
            return p;
        }, {...state.staleIds}); 

        console.log('NEW STALE IDS', newStaleIds);

        return {
            ...state,
            lines: updatedLines,
            staleIds: newStaleIds
        }
    }),
    on(bulkUpdateLinesFailure, (state, { error }) => {
        return {
            ...state,
            loading: false,
            error
        }
    }),
    on(bulkUpdateLinesSuccess, (state, { lines }) => {
        // const updatedIds = lines.map(m => m.id);
        // const untouchedLines = state.lines.filter(f => !updatedIds.includes(f.id));

        // Convert the data model to the view model
        // const updatedState = createViewModel(state);

        return { ...state };
    }),
    on(bulkUpdateTailLines, (state, { lines }) => {
        console.debug('[REDUCER] bulkUpdateTailLines ontvangen', lines);
        const lineIds = lines.map(m => m.id);
        const untouchedLines = state.tail.filter(f => !lineIds.includes(f.id));
        const updatedLines = [...untouchedLines, ...lines];
        
        // Check if the line is being created, if so; add to the staleIds
        const newStaleIds = lines.reduce<{ [key: string]: boolean }>((p, c) => {
            if (state.creatingIds[c.id]) {
                p[c.id] = true;
            }
            return p;
        }, {...state.staleIds}); 

        console.log('NEW STALE IDS', newStaleIds);

        return {
            ...state,
            tail: updatedLines,
            staleIds: newStaleIds
        };
    }),
    on(bulkUpdateTailLinesFailure, (state, { error }) => {
        return {
            ...state,
            loading: false,
            error
        }
    }),
    on(bulkUpdateLinesSuccess, (state, { lines }) => {
        // const updatedIds = lines.map(m => m.id);
        // const untouchedLines = state.lines.filter(f => !updatedIds.includes(f.id));

        // Convert the data model to the view model
        const updatedState = createViewModel(state);

        return {
            ...updatedState
        }
    }),
    on(bulkInsertTailLines, (state, { lines }) => {
        const lineIds = lines.map(m => m.id);
        const untouchedLines = state.tail.filter(f => !lineIds.includes(f.id));
        const tail = [...untouchedLines, ...lines];
        return createViewModel({
            ...state,
            tail,
            creatingIds: {
                ...state.creatingIds,
                ...Object.fromEntries(lines.map(line => [line.id, true]))
            }
        })
    }),
    on(bulkInsertTailLinesSuccess, (state, { lines }) => {
        const ids = lines.map(line => line.id)

        return {
            ...state,
            creatingIds: removeKeys(state.creatingIds, ids),
        }
    }),
    on(bulkInsertTailLinesFailure, (state, { error }) => {
        return {
            ...state,
            error
        }
    }),
    on(pdfExport, (state, { options }) => {
        return {
            ...state,
            loading: true
        }
    }),
    on(pdfExportCreationSuccess, (state, { file }) => {
        return {
            ...state
        }
    }),
    on(pdfExportDownloadSuccess, (state) => {
        return {
            ...state,
            loading: false
        }
    }),
    on(pdfExportFailure, (state, { error }) => {
        return {
            ...state,
            loading: false,
            error
        }
    }),
    on(startScrolling, (state) => {
        return {
            ...state,
            // scrolling: true
        }
    }),
    on(updateVisibleParts, (state, { show }) => {
        // console.log('SHOW', show);
        return {
            ...state,
            show
        }
    }),
    on(updateTableLayout, (state, { layout }) => {
        // console.log('LAYOUT', layout);
        return {
            ...state,
            columns: layout === 'default' ? DEFAULT_COLUMN_DEFINITION : CONDENSED_COLUMN_DEFINITION
        } satisfies CalculationState
    }),
    on(showSideMenu, (state) => {
        // console.log('SHOW SIDEMENU', state.sideMenuOpen);
        return {
            ...state,
            sideMenuOpen: true
        }
    }),
    on(hideSideMenu, (state) => {
        // console.log('HIDE SIDEMENU', state.sideMenuOpen);
        return {
            ...state,
            sideMenuOpen: false
        }
    })
);

// Helper functions
function isSameCalculation(state: CalculationState, newMeta: CalculationMetaApi) {
    // Not same calculation if one of the parameters is undefined or null
    if (!state) {
        return false;
    }

    // Calculation and project id must be the same. Version is ignored
    if (state.meta?.id !== newMeta.id || state.meta?.projectId !== newMeta.projectId) {
        return false;
    }

    return true;
}

function convertTemplateToDict(template: TemplateCategoryApi[]) {
    // Create a dictionary for the category names
    return template.reduce<{ [code: string]: string }>((p, c) => {
        p[c.categoryCode] = c.categoryName;

        for (const sub of c.subcategories) {
            p[sub.subcategoryCode] = sub.subcategoryName
        }
        return p;
    }, {});
}

function findFirstSelectableRowId(state: CalculationState, up: boolean = true) {
    let selectedRow = state.currentSelection.current.row;

    const eligibleRowTypes = (Object.keys(state.columns) as ViewModelLineType[])
        .filter(RowType => Object.keys(state.columns[RowType] as ColumnDefinition)
            .some(key => {
                const column = state.columns[RowType][key];

                return column.focusable && !column.hidden && column.selectable
            }))

    while (
        selectedRow >= 0 && // Should have valid index (0 or greater)
        selectedRow < state.viewModelLines.length && // Should not be beyond the length of the rows array
        !eligibleRowTypes.includes(state.viewModelLines[selectedRow].type) // Should be of an eligible row type, that is; with selectable columns so it can get focus
    ) {
        selectedRow += up ? -1 : 1;
    }

    if (selectedRow >= state.viewModelLines.length || selectedRow < 0) {
        return null;
    }
    else {
        return selectedRow;
    }
}

function findNextSelectableRowId(state: CalculationState, up: boolean = true, selectedLines?: (CalculationLineApi | CalculationTailApi)[]) {
    let selectedRow = state.currentSelection.current.row;
    const deletedRowIds = (selectedLines ?? []).map(m => m.id);
    const deletedRowIndices = deletedRowIds.map(id => state.viewModelLines.find(l => l.line?.id === id)?.index).filter(Boolean);

    const eligibleRowTypes = (Object.keys(state.columns) as ViewModelLineType[])
        .filter(RowType => Object.keys(state.columns[RowType] as ColumnDefinition)
            .some(key => {
                const column = state.columns[RowType][key];

                return column.focusable && !column.hidden && column.selectable
            }))

    do {
        selectedRow += up ? -1 : 1;
    } while (
        selectedRow >= 0 && // Should have valid index (0 or greater)
        selectedRow < state.viewModelLines.length && // Should not be beyond the length of the rows array
        (
            deletedRowIndices.includes(selectedRow) || // Should not be one of the deleted rows
            !eligibleRowTypes.includes(state.viewModelLines[selectedRow].type) // Should be of an eligible row type, that is; with selectable columns so it can get focus
        )
    );

    if (selectedRow >= state.viewModelLines.length || selectedRow < 0) {
        return null;
    }
    else {
        return selectedRow;
    }
}

function mapToClosestColumn(currentColumnDefinition: ColumnDefinition, currentColumnIndex: number, newColumnDefinition: ColumnDefinition) {
    return getClosestColumn(newColumnDefinition, getColumnLeftPosition(currentColumnDefinition, currentColumnIndex));
}

/**
 * Find the position of the column by adding the widths of the columns to the left
 * @param ColumnDefinition definitions of all columns
 * @param columnIndex the position is calculated for the column with this index
 * @returns 
 */
function getColumnLeftPosition(ColumnDefinition: ColumnDefinition, columnIndex: number) {
    let percentageSum = 0;
    Object.keys(ColumnDefinition).find(key => {
        if (ColumnDefinition[key].index < columnIndex) {
            percentageSum += ColumnDefinition[key].width;
            return false;
        }
        return true
    });

    return percentageSum;
}

/**
 * Find the column in the given definition which is closest to the given position
 * Only looking at the left position of the column
 * @param ColumnDefinition definition of all columns
 * @param position find the column with the left closest to this position
 * @returns 
 */
export function getClosestColumn(ColumnDefinition: ColumnDefinition, position: number) {
    let columnLeftPercentageCur = 0;
    let currentlyClosest = Number.MAX_VALUE;

    // Get the column closest to the last selection
    let targetColumn = Object.keys(ColumnDefinition).reduce<ColumnFormat>((p, c) => {
        const thisColumn = ColumnDefinition[c];
        const diff = Math.abs(columnLeftPercentageCur - position);

        // Add column width
        columnLeftPercentageCur += thisColumn.width;

        if (thisColumn.hidden || !thisColumn.focusable || !thisColumn.selectable || diff >= currentlyClosest) {
            return p;
        } else {
            currentlyClosest = diff;
            return thisColumn;
        }
    }, undefined);

    return targetColumn;
}

/**
 * Find the first valid selection with the current selection as reference
 * Valid means finding a row that can get focus and is selectable
 * @param state 
 * @param up 
 * @returns 
 */
function findFirstRowSelection(state: CalculationState, up: boolean = true) {
    // Get the view model line. This includes rendering info like indices
    let prevSelectedViewLine = state.viewModelLines[state.currentSelection.current.row];
    // Based on the type of the view line, find the column definitions
    const selectedColumns = state.columns[prevSelectedViewLine.type];
    // To select a new column close to the current, find the active column
    // Assumption is that the columns are sorted by index
    let columnLeftPercentagePrev = getColumnLeftPosition(selectedColumns, state.currentSelection.current.column);

    const nextRowId = findFirstSelectableRowId(state, up);

    // console.log(nextRowId);

    if (nextRowId === null) {
        return {
            ...state
        };
    } else {
        let nextSelectedViewLine = state.viewModelLines[nextRowId];

        // Get the column definition
        const colDef = state.columns[nextSelectedViewLine.type];

        // Get the column closest to the last selection
        let targetColumn = getClosestColumn(colDef, columnLeftPercentagePrev);

        // Find the closest to the active one
        return {
            ...state,
            currentSelection: {
                ...state.currentSelection,
                current: {
                    column: targetColumn.index,
                    row: nextRowId
                },
                selectedLines: [nextSelectedViewLine.line]
            }
        } satisfies CalculationState
    }
}

/**
 * Find the next row that is a valid selection, starting from the current selection
 * This will not be the currently selected row, unlike with findFirstRowSelection
 * @param state 
 * @param up 
 * @returns 
 */
function findNextRowSelection(state: CalculationState, up: boolean = true) {
    // Get the view model line. This includes rendering info like indices
    let prevSelectedViewLine = state.viewModelLines[state.currentSelection.current.row];
    // Based on the type of the view line, find the column definitions
    const selectedColumns = state.columns[prevSelectedViewLine.type];
    // To select a new column close to the current, find the active column
    // Assumption is that the columns are sorted by index
    let columnLeftPercentagePrev = getColumnLeftPosition(selectedColumns, state.currentSelection.current.column);

    const nextRowId = findNextSelectableRowId(state, up);

    // console.log(nextRowId);

    if (nextRowId === null) {
        return {
            ...state
        };
    } else {
        let nextSelectedViewLine = state.viewModelLines[nextRowId];

        // Get the column definition
        const colDef = state.columns[nextSelectedViewLine.type];

        // Get the column closest to the last selection
        let targetColumn = getClosestColumn(colDef, columnLeftPercentagePrev);

        // Find the closest to the active one
        return {
            ...state,
            currentSelection: {
                ...state.currentSelection,
                current: {
                    column: targetColumn.index,
                    row: nextRowId
                },
                selectedLines: [nextSelectedViewLine.line]
            }
        } satisfies CalculationState
    }
}

function updateSelectionOnDeletion(state: CalculationState, selectedLines?: (CalculationLineApi | CalculationTailApi)[]) {
    if (!state.currentSelection?.current) {
        return { ...state }
    }

    const currentViewLine = state.viewModelLines[state.currentSelection.current.row];

    const currentColumnIndex = state.currentSelection.current.column;
    const currentColDef = state.columns[currentViewLine.type];

    const selectAbove = findNextSelectableRowId(state, true, selectedLines);
    const selectBelow = findNextSelectableRowId(state, false, selectedLines);

    // Nothing to select
    if (!selectAbove && !selectBelow) {
        return {
            ...state,
            currentSelection: undefined
        }
    }

    let nextSelectedRow: number;

    if (isCalculationLineApi(currentViewLine.line)) {
        // Use the line below if it is the same category
        const currentCategory = currentViewLine.line.code;
        if (selectBelow && isCalculationLineApi(state.viewModelLines[selectBelow].line) && (state.viewModelLines[selectBelow].line as CalculationLineApi).code === currentCategory) {
            nextSelectedRow = selectBelow;
        } else if (selectAbove && isCalculationLineApi(state.viewModelLines[selectAbove].line) && (state.viewModelLines[selectAbove].line as CalculationLineApi).code === currentCategory) {
            // Or above if same category
            nextSelectedRow = selectAbove;
        } else {
            // If no other lines were found with the same category, take either
            nextSelectedRow = selectAbove ?? selectBelow;
        }
    } else {
        nextSelectedRow = selectBelow ?? selectAbove;
    }

    // console.log('SELECTED', state.viewModelLines[nextSelectedRow]);

    const newSelection: CalculationSelection = {
        current: {
            row: nextSelectedRow,
            column: mapToClosestColumn(
                currentColDef,
                currentColumnIndex,
                state.columns[state.viewModelLines[nextSelectedRow].type]).index
        },
        selectedLines: [state.viewModelLines[nextSelectedRow].line]
    };

    return {
        ...state,
        currentSelection: newSelection
    }
}

function removeKey<T>(record: Record<string, T>, key: string): Record<string, T> {
    const { [key]: _, ...rest } = record;
    return rest;
}

function removeKeys<T>(obj: Record<string, T>, keysToRemove: string[]): Record<string, T> {
    return Object.fromEntries(
        Object.entries(obj).filter(([key]) => !keysToRemove.includes(key))
    )
}

